import { Button, Modal } from 'antd'
import React from 'react'
import { MODAL_BREDD, MODAL_GRIND, MODAL_KNAPP, MODAL_KNAPP_FARLIG, modalTitel } from '../modalStil'

/**
 * AvbrytModal – vägvalet när ett pågående besök ska lämnas.
 *
 * Avbryt-knappen ligger intill Spara i knappraden och når man med tummen mitt i ett
 * samtal. Utan bekräftelse räcker en felträff för att kasta ett helt hembesök.
 *
 * Tre utfall, inte två. Det vanligaste skälet att lämna ett besök i förtid är att
 * brukaren blir trött – och då ska arbetet inte behöva göras om. Att pausa hör därför
 * hemma just här, i stället för som en knapp som ligger framme hela besöket och
 * inbjuder till att pausa i onödan.
 *
 * Tre jämbördiga knappar i stället för en kryssruta som ändrar vad huvudknappen gör.
 * Var och en säger vad den gör, och inget av utfallen är "det vanliga".
 *
 * Alla tre är imperativ i presens med parallella objekt. Ordet är AVBRYT, inte avsluta:
 * "avsluta besöket" läses lika gärna som "slutför", vilket är motsatsen till vad
 * knappen gör – och intill en Spara-knapp skulle de två se ut som varsitt sätt att
 * bli klar.
 */

export interface AvbrytModalProps {
  open: boolean
  personnummer: string
  /** Spara som utkast och gå tillbaka till översikten. */
  onPausa: () => void
  /** Avbryt besöket – ingenting sparas. */
  onKasta: () => void
  /** Ångrat – tillbaka till besöket. */
  onFortsatt: () => void
  sparar?: boolean
}

const AvbrytModal: React.FC<AvbrytModalProps> = ({
  open,
  personnummer,
  onPausa,
  onKasta,
  onFortsatt,
  sparar,
}) => (
  <Modal
    open={open}
    {...MODAL_GRIND}
    title={modalTitel('Vad vill du göra med besöket?')}
    width={MODAL_BREDD}
    onCancel={onFortsatt}
    // Egen fot: Ant Designs ok/cancel rymmer bara två utfall.
    footer={
      <div className="flex flex-wrap justify-end gap-2">
        <Button {...MODAL_KNAPP} onClick={onFortsatt}>
          Fortsätt besöket
        </Button>
        <Button {...MODAL_KNAPP_FARLIG} onClick={onKasta}>
          Avbryt besöket
        </Button>
        <Button {...MODAL_KNAPP} type="primary" loading={sparar} onClick={onPausa}>
          Spara som utkast
        </Button>
      </div>
    }
  >
    <p className="m-0 text-base text-gray-700">
      Besöket för <span className="font-medium">{personnummer}</span> är inte klart.
    </p>

    <div className="mt-4 flex flex-col gap-2">
      <p className="m-0 text-base text-gray-700">
        <span className="font-medium">Spara som utkast</span> behåller svaren. Ni återupptar där ni slutade, och
        ingenting räknas förrän besöket sparats på riktigt – inget index, inga nya mål.
      </p>
      <p className="m-0 text-base text-gray-700">
        <span className="font-medium">Avbryt besöket</span> slänger allt ni gått igenom. Det går inte att ångra.
      </p>
    </div>
  </Modal>
)

export default AvbrytModal
